import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { useToast } from '../../contexts/ToastContext';
import '../../styles/EnterpriseAdminOnboarding.css';

const STEPS = 4;

/**
 * Full-screen onboarding for enterprise_admin first visit (per enterprise).
 */
export default function EnterpriseAdminOnboarding({ enterpriseId, companyName, onFinished }) {
  const navigate = useNavigate();
  const { showError } = useToast();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const base = `/enterprise-app/${enterpriseId}`;

  const patchOnboarding = useCallback(
    async (action) => {
      await api.patch('/api/enterprise/onboarding', { enterpriseId, action });
    },
    [enterpriseId]
  );

  const finishWithAction = useCallback(
    async (action) => {
      setSubmitting(true);
      try {
        await patchOnboarding(action);
        onFinished?.();
      } catch (err) {
        showError(err.response?.data?.error || 'Could not save your preference');
      } finally {
        setSubmitting(false);
      }
    },
    [patchOnboarding, onFinished, showError]
  );

  const handleDismiss = () => finishWithAction('dismiss');

  /** Marks onboarding complete, navigates, refreshes membership state. */
  const completeAndNavigate = async (path) => {
    setSubmitting(true);
    try {
      await patchOnboarding('complete');
      navigate(path);
      onFinished?.();
    } catch (err) {
      showError(err.response?.data?.error || 'Could not save your preference');
    } finally {
      setSubmitting(false);
    }
  };

  const next = () => setStep((s) => Math.min(s + 1, STEPS - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const name = companyName || 'your company';

  let content = null;
  if (step === 0) {
    content = (
      <>
        <p className="ent-onb-eyebrow">Velox Enterprise</p>
        <h2 className="ent-onb-title">Welcome, Enterprise Admin</h2>
        <p className="ent-onb-lead">
          You&apos;re set up as an <strong>Enterprise Admin</strong> for <strong>{name}</strong>. Velox Enterprise
          helps your organisation roll out digital business cards, track engagement, and see how teams are performing —
          all in one place.
        </p>
        <p className="ent-onb-lead" style={{ marginBottom: 0 }}>
          You oversee how Velox is used across the company and who has access. Take a minute to see what you can do,
          then jump into the dashboard when you&apos;re ready.
        </p>
      </>
    );
  } else if (step === 1) {
    content = (
      <>
        <p className="ent-onb-eyebrow">Capabilities</p>
        <h2 className="ent-onb-title">What you can do</h2>
        <ul className="ent-onb-list">
          <li>
            <strong>Invite team members</strong> — grow adoption with email-bound invitations.
          </li>
          <li>
            <strong>Organise teams</strong> — group people for structure and reporting.
          </li>
          <li>
            <strong>Monitor engagement</strong> — see activity and performance at team level.
          </li>
          <li>
            <strong>Analytics &amp; insights</strong> — understand usage and connections across the company.
          </li>
        </ul>
      </>
    );
  } else if (step === 2) {
    content = (
      <>
        <p className="ent-onb-eyebrow">Recommended</p>
        <h2 className="ent-onb-title">Start strong</h2>
        <p className="ent-onb-lead">A practical order that works for most companies:</p>
        <ul className="ent-onb-list">
          <li>
            Create your first <strong>team</strong> (or a few) to mirror how you work.
          </li>
          <li>
            <strong>Invite</strong> colleagues so they can activate cards and appear in your data.
          </li>
          <li>
            Open the <strong>overview</strong> to see adoption and engagement build over time.
          </li>
          <li>
            Use <strong>Accounts</strong> when you need to support members or review who&apos;s in.
          </li>
        </ul>
      </>
    );
  } else {
    content = (
      <>
        <p className="ent-onb-eyebrow">You&apos;re ready</p>
        <h2 className="ent-onb-title">You&apos;re set up</h2>
        <p className="ent-onb-lead">
          Your enterprise workspace is live. Most admins start by inviting colleagues so adoption and analytics reflect
          your real team. You can also go straight to the overview whenever you prefer — teams, accounts, and invitations
          are always available from the sidebar.
        </p>
        <div className="ent-onb-final-actions">
          <button
            type="button"
            className="ent-onb-btn ent-onb-btn-primary ent-onb-final-primary"
            onClick={() => completeAndNavigate(`${base}/invites`)}
            disabled={submitting}
          >
            {submitting ? 'Saving…' : 'Invite Team Members'}
          </button>
          <button
            type="button"
            className="ent-onb-btn ent-onb-btn-secondary ent-onb-final-secondary"
            onClick={() => completeAndNavigate(`${base}/dashboard`)}
            disabled={submitting}
          >
            Enter Dashboard
          </button>
        </div>
      </>
    );
  }

  return (
    <div className="ent-onb-overlay" role="dialog" aria-modal="true" aria-label="Enterprise admin onboarding">
      <div className="ent-onb-panel">
        <div className="ent-onb-progress" aria-hidden="true">
          {Array.from({ length: STEPS }, (_, i) => (
            <span key={i} className={`ent-onb-dot${i === step ? ' active' : ''}`} />
          ))}
        </div>
        <div className="ent-onb-body">{content}</div>
        <div className="ent-onb-footer">
          {step > 0 ? (
            <button type="button" className="ent-onb-btn ent-onb-btn-ghost" onClick={back} disabled={submitting}>
              Back
            </button>
          ) : (
            <span />
          )}
          <div className="ent-onb-footer-spacer" />
          <button
            type="button"
            className="ent-onb-btn ent-onb-btn-secondary"
            onClick={handleDismiss}
            disabled={submitting}
          >
            Skip for now
          </button>
          {step < STEPS - 1 ? (
            <button type="button" className="ent-onb-btn ent-onb-btn-primary" onClick={next} disabled={submitting}>
              Continue
            </button>
          ) : null}
          <p className="ent-onb-hint">You can replay this tour anytime from the sidebar.</p>
        </div>
      </div>
    </div>
  );
}
